// src/hooks/useLeagueCreation.js
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { leaguesAPI } from '../services/apiService';

export const useLeagueCreation = () => {
  const navigate = useNavigate();
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState('');
  const [creationStatus, setCreationStatus] = useState(null);
  const [pollingInterval, setPollingInterval] = useState(null);
  
  const [formData, setFormData] = useState({
    // Basic Info
    league_name: '',
    max_teams: 12,
    player_pool: 'american_national',
    include_minor_leagues: false,
    
    // League Type & Draft Configuration (NEW - Phase 3)
    league_type: 'dynasty',  // 'dynasty' or 'redraft' (UI shows "Single Season")
    num_keepers: 15,  // 0-23, default 15 for dynasty
    draft_rounds: 23,  // 1-40, default 23
    ml_draft_rounds: 2,  // 1-10, default 2

    // Dynasty draft types
    dynasty_initial_draft_type: 'snake',  // first year draft
    dynasty_subsequent_draft_type: 'basic_standings',  // year 2+ draft
    
    // Redraft draft type (for "Single Season" leagues)
    redraft_initial_draft_type: 'snake',
    
    // Lottery settings (dynasty only, when subsequent_draft_type = 'standings_lottery')
    lottery_num_picks: 3,  // 1-5, default 3
    lottery_top_excluded: 3,  // 0-10, default 3
    // Note: lottery_enabled and lottery_weighting auto-set by backend
    
    // Scoring
    scoring_system: 'rotisserie_ytd',
    scoring_categories: {
      hitting: ['R', 'HR', 'RBI', 'SB', 'AVG', 'OPS'],
      pitching: ['W', 'SV', 'ERA', 'WHIP', 'SO', 'QS']
    },
    
    // Rosters - CRITICAL: This needs to be saved to league_settings
    max_players_total: 23,
    min_hitters: 13,
    max_pitchers: 10,
    min_pitchers: 10,
    bench_slots: 5,
    dl_slots: 0,
    minor_league_slots: 0,
    position_requirements: {
      'C': { slots: 2 },
      '1B': { slots: 1 },
      '2B': { slots: 1 },
      '3B': { slots: 1 },
      'SS': { slots: 1 },
      'OF': { slots: 4 },
      'MI': { slots: 1 },
      'CI': { slots: 1 },
      'UTIL': { slots: 1 },
      'P': { slots: 10 }
    },
    
    // Financial
    use_salaries: true,
    use_contracts: true,
    use_dual_cap: true,
    draft_cap: 600,
    season_cap: 200,
    salary_cap: 800,
    standard_contract_length: 2,
    min_salary: 2,
    salary_increment: 2,
    rookie_price: 20,
    draft_cap_usage: 0.75,
    
    // Advanced
    transaction_deadline: 'none',
    use_waivers: false,
    // Season dates determined automatically from first/last game logs in database
    season_start_date: null,
    season_end_date: null
  });

  // Cleanup polling interval on unmount
  useEffect(() => {
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [pollingInterval]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setError('');
  };

  const handleCategoryChange = (type, categories) => {
    setFormData(prev => ({
      ...prev,
      scoring_categories: {
        ...prev.scoring_categories,
        [type]: categories
      }
    }));
  };

  const handlePositionChange = (position, field, value) => {
    setFormData(prev => ({
      ...prev,
      position_requirements: {
        ...prev.position_requirements,
        [position]: {
          ...prev.position_requirements[position],
          [field]: parseInt(value) || 0
        }
      }
    }));
  };

  const validateCurrentStep = (step) => {
    switch (step) {
      case 1: // Basic Info
        if (!formData.league_name || formData.league_name.length < 3) {
          setError('League name must be at least 3 characters long');
          return false;
        }
        break;
      
      case 2: // League Type & Draft (NEW)
        if (!formData.league_type) {
          setError('Please select a league type');
          return false;
        }
        
        if (formData.draft_rounds < 1 || formData.draft_rounds > 40) {
          setError('Draft rounds must be between 1 and 40');
          return false;
        }
        
        if (formData.num_keepers < 0 || formData.num_keepers > formData.draft_rounds) {
          setError('Number of keepers must be between 0 and draft rounds');
          return false;
        }
        
        if (formData.league_type === 'dynasty') {
          if (!formData.dynasty_initial_draft_type) {
            setError('Please select an initial draft type');
            return false;
          }
          if (!formData.dynasty_subsequent_draft_type) {
            setError('Please select a subsequent draft type');
            return false;
          }
          
          if (formData.dynasty_subsequent_draft_type === 'standings_lottery') {
            const maxLotteryPicks = formData.max_teams - (formData.lottery_top_excluded ?? 0);
            if (formData.lottery_num_picks < 1 || formData.lottery_num_picks > maxLotteryPicks) {
              setError(`Lottery picks must be between 1 and ${maxLotteryPicks}`);
              return false;
            }
            if (formData.lottery_top_excluded < 0 || formData.lottery_top_excluded > 10) {
              setError('Top teams excluded must be between 0 and 10');
              return false;
            }
          }
        }
        
        if (formData.league_type === 'redraft') {
          if (!formData.redraft_initial_draft_type) {
            setError('Please select a draft type');
            return false;
          }
        }
        break;
      
      case 3: // Scoring (was case 2)
        if (formData.scoring_categories.hitting.length === 0) {
          setError('Please select at least one hitting category');
          return false;
        }
        if (formData.scoring_categories.pitching.length === 0) {
          setError('Please select at least one pitching category');
          return false;
        }
        break;
      
      case 4: // Rosters (was case 3)
        if (formData.max_players_total < 15 || formData.max_players_total > 40) {
          setError('Total players must be between 15 and 40');
          return false;
        }
        break;
      
      case 5: // Financial (was case 4)
        if (formData.use_salaries && formData.use_dual_cap) {
          if (formData.draft_cap <= 0) {
            setError('Draft cap must be greater than 0');
            return false;
          }
        }
        break;
      
      case 6: // Trades (was case 5)
        if (formData.season_start_date && formData.season_end_date) {
          const startDate = new Date(formData.season_start_date);
          const endDate = new Date(formData.season_end_date);
          if (startDate >= endDate) {
            setError('Season end date must be after start date');
            return false;
          }
        }
        break;
    }
    setError('');
    return true;
  };

  // Status polling function
  const pollLeagueStatus = async (leagueId) => {
  try {
  const statusResponse = await leaguesAPI.checkLeagueCreationStatus(leagueId);

  if (statusResponse && statusResponse.status) {
  // Map backend status to user-friendly messages
        const statusMessages = {
          'initializing': { progress: 30, message: 'Creating league structure...' },
          'populating_stats': { progress: 70, message: 'Populating player statistics...' },
          'completed': { progress: 100, message: 'League creation complete!' },
          'failed': { progress: 0, message: 'League creation failed' }
        };

        const mappedStatus = statusMessages[statusResponse.status] || statusResponse;
        setCreationStatus({ ...statusResponse, ...mappedStatus });
        
        if (statusResponse.status === 'completed') {
          if (pollingInterval) {
            clearInterval(pollingInterval);
            setPollingInterval(null);
          }
          
          setTimeout(() => {
            setIsCreating(false);
            navigate(`/leagues/${leagueId}/welcome`);
          }, 2000);
          
        } else if (statusResponse.status === 'failed') {
          if (pollingInterval) {
            clearInterval(pollingInterval);
            setPollingInterval(null);
          }
          
          setError(statusResponse.message || statusResponse.error || 'League creation failed');
          setIsCreating(false);
        }
      }
    } catch (error) {
      console.error('Status polling error:', error);
    }
  };

  const handleSubmit = async (formData) => {
    // Validate final step (Advanced is now step 7)
    if (!validateCurrentStep(7)) {
      return;
    }

    setIsCreating(true);
    setError('');
    setCreationStatus({ status: 'initializing', progress: 5, message: 'Starting league creation...' });
    
    try {
      // Construct payload with league type fields
      const payload = {
        ...formData,
        
        // Add dynasty-specific fields
        ...(formData.league_type === 'dynasty' && {
          num_keepers: formData.num_keepers,
          dynasty_initial_draft_type: formData.dynasty_initial_draft_type,
          dynasty_subsequent_draft_type: formData.dynasty_subsequent_draft_type,
          
          // Add lottery settings if applicable
          ...(formData.dynasty_subsequent_draft_type === 'standings_lottery' && {
            lottery_weighting: 'nba_style',  // Only option currently
            lottery_num_picks: formData.lottery_num_picks,
            lottery_top_excluded: formData.lottery_top_excluded
          })
        }),
        
        // Add redraft-specific fields
        ...(formData.league_type === 'redraft' && {
          num_keepers: 0,  // Redraft leagues have no keepers
          redraft_initial_draft_type: formData.redraft_initial_draft_type
        })
      };
      
      console.log('Creating league with data:', payload);
      
      const response = await leaguesAPI.createLeague(payload);
      
      if (response.success && response.league_id) {
        const interval = setInterval(() => {
          pollLeagueStatus(response.league_id);
        }, 3000);
        
        setPollingInterval(interval);
        pollLeagueStatus(response.league_id);
        
      } else {
        throw new Error(response.message || 'Failed to start league creation');
      }
    } catch (error) {
      console.error('League creation error:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to create league';
      setError(errorMessage);
      setIsCreating(false);
      setCreationStatus(null);
    }
  };

  return {
    formData,
    isCreating,
    error,
    creationStatus,
    handleInputChange,
    handleCategoryChange,
    handlePositionChange,
    validateCurrentStep,
    handleSubmit,
    setError
  };
};